// Static data — services, suburbs, testimonials, sample gallery.

export const COMPANY = {
  name: "Connolly's Painting Solutions",
  short: "Connolly's Painting",
  tagline: "Sunshine Coast's premium painting specialists.",
  phone: "0401 437 582",
  phoneTel: "+61401437582",
  email: "nick@connollyspaintingsolutions.com.au",
  area: "Sunshine Coast, QLD",
  abn: "14 516 161 647",
  qbcc: "15553095",
  address: {
    street: "Sunshine Coast",
    locality: "Maroochydore",
    region: "QLD",
    postal: "4558",
    country: "AU",
  },
  hours: "Mon–Sat 7:00–17:00",
  social: {
    instagram: "#",
    facebook: "#",
  },
};

export const SERVICES = [
  {
    slug: "interior-painting",
    name: "Interior Painting",
    title: "Interior Painting — Sunshine Coast",
    seoTitle: "Interior Painters Sunshine Coast",
    short: "Walls, ceilings, trims and feature finishes — flawless interiors that feel calm, considered and built to last.",
    hero: "https://images.unsplash.com/photo-1724582586458-a51791349977?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    details: [
      "Interior repaints are where small details add up — straight lines, even sheen, no roller marks, no missed corners. Our process starts with a detailed walk-through of every room, identifying patch repairs, water marks, hairline cracks and trim damage before we quote.",
      "Once on site we mask, drop-sheet and move furniture before any paint goes near a wall. Filling, sanding and undercoating are done properly — not skipped to save time. We use premium ranges from Dulux, Haymes and Resene, specified to suit each room (low-sheen for living, satin or semi-gloss for trims and high-wear areas, washable acrylics for kitchens and kids' rooms).",
      "Final clean-down is included. You walk through the finished job with the lead painter before sign-off — we don't leave until you're happy.",
    ],
    images: [
      "/api/uploads/client/buderim-after.jpg",
      "/api/uploads/client/mooloolaba-after.jpg",
      "/api/uploads/client/newbuild-after.jpg",
      "https://images.unsplash.com/photo-1724582586458-a51791349977?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
    bullets: [
      "Premium low-VOC paints — Dulux, Haymes, Resene",
      "Detailed prep: filling, sanding, undercoating",
      "Furniture protection and tidy daily site management",
      "Crisp cutting-in, no roller marks, perfect coverage",
    ],
    faqs: [
      { q: "How long does an interior repaint take?", a: "A typical 3–4 bedroom home interior takes 4–7 working days depending on prep, ceiling work and access. We'll give you a precise schedule with your quote." },
      { q: "Do you move furniture?", a: "Yes — light furniture is moved and covered. Heavy or fragile items we'll plan with you in advance." },
      { q: "Which paint brands do you use?", a: "Premium ranges from Dulux, Haymes and Resene. We recommend a finish appropriate to the room — low-sheen for living, satin or semi-gloss for trims, washable for high-traffic." },
      { q: "Do you provide a colour consultation?", a: "Yes. Complimentary colour consult on bookings over $5,000 — or paid consult available on smaller jobs." },
    ],
  },
  {
    slug: "exterior-painting",
    name: "Exterior Painting",
    title: "Exterior House Painting — Sunshine Coast",
    seoTitle: "Exterior Painters Sunshine Coast",
    short: "Coastal-ready exterior systems. Sealed, primed, finished — protection that handles salt air, UV and storm season.",
    hero: "https://images.unsplash.com/photo-1760129745103-91c4022ed5fb?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    details: [
      "The Sunshine Coast is one of the toughest climates in Australia for exterior paint. Salt-laden air, near-vertical UV and a long humid wet season will pull apart a cheap exterior job in 2–3 years. We specify coastal-grade systems designed for the climate, and we apply them properly.",
      "Every exterior repaint starts with a high-pressure clean and full inspection — flaking paint scraped back, timber repaired or replaced, gaps caulked, render hairline cracks filled, exposed metal primed against rust. Only then do we start applying coatings.",
      "We use UV-stable, mould-resistant exterior acrylics in multi-coat systems with manufacturer-backed product warranties. The result: a finish that still looks crisp 6–8 years later, even on salt-exposed properties.",
    ],
    images: [
      "/api/uploads/client/noosa-after.jpg",
      "https://images.unsplash.com/photo-1760129745103-91c4022ed5fb?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.pexels.com/photos/15243628/pexels-photo-15243628.jpeg?auto=compress&cs=tinysrgb&w=1400",
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    ],
    bullets: [
      "High-pressure clean and full surface preparation",
      "Caulking, gap-filling and timber repairs",
      "UV-stable, mould-resistant exterior coatings",
      "Multi-coat systems with manufacturer-backed warranties",
    ],
    faqs: [
      { q: "How often should I repaint my exterior on the Sunshine Coast?", a: "Coastal exposure shortens paint life. Most homes benefit from a repaint every 7–10 years; salt-exposed properties closer to 6–8 years." },
      { q: "Do you paint render and weatherboard?", a: "Yes — render, weatherboard, fibre cement, timber and most metal substrates. We match the prep and coating system to the surface." },
      { q: "Will weather delay the job?", a: "We schedule around the forecast and only apply coatings within manufacturer-spec temperature and humidity windows. You get a clear timeline with daily updates." },
    ],
  },
  {
    slug: "roof-painting-sunshine-coast",
    name: "Roof Painting",
    title: "Roof Painting & Restoration — Sunshine Coast",
    seoTitle: "Roof Painters Sunshine Coast",
    short: "Tile and metal roof restoration with high-build membranes. Sealed, colour-true, warrantied.",
    hero: "https://images.unsplash.com/photo-1566519648732-ba06372eb7b0?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    card: "/api/uploads/client/roof-card.jpg",
    details: [
      "Roof restoration is the highest-value repaint a Sunshine Coast home can have. A faded, mossy or rusting roof drops kerb appeal and lets heat into the home. A properly restored roof looks new from the street and reflects more solar heat — sometimes shaving degrees off the rooms below.",
      "We start with a thorough roof inspection: cracked or slipped tiles, failing ridge caps, rusting metal sheets, blocked valleys. Tile roofs get re-bedded and re-pointed where needed; metal roofs are treated for any rust and primed against future corrosion. The whole roof is then high-pressure cleaned and treated for moss, lichen and biological growth.",
      "Once the surface is sound and sealed, we apply a high-build membrane system in your choice of colour — including heat-reflective options. Finished, walked through with you, and signed off.",
    ],
    images: [
      "/api/uploads/client/caloundra-roof-after.jpg",
      "/api/uploads/client/roof-card.jpg",
      "https://images.unsplash.com/photo-1566519648732-ba06372eb7b0?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1564540583246-934409427776?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    ],
    bullets: [
      "High-pressure clean and moss/lichen treatment",
      "Re-bedding & re-pointing of ridge caps where required",
      "Heat-reflective membrane systems",
      "Premium paint systems applied in spec",
    ],
    faqs: [
      { q: "Can you paint a Colorbond roof?", a: "Yes — Colorbond and metal roofs are pressure-cleaned, treated for any rust, primed and finished with a flexible UV-stable membrane." },
      { q: "How long does a roof restoration take?", a: "Most roofs take 3–5 days depending on size, repairs and weather. Tile roofs with pointing repairs are typically longer than metal." },
    ],
  },
  {
    slug: "commercial-painting",
    name: "Commercial Painting",
    title: "Commercial Painting — Sunshine Coast",
    seoTitle: "Commercial Painters Sunshine Coast",
    short: "After-hours and weekend trades. Retail, hospitality, strata, body corporate, fit-outs.",
    hero: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    details: [
      "Commercial painting on the Sunshine Coast covers everything from a single-shop refresh in Mooloolaba to a multi-storey body corporate repaint in Maroochydore. We work with property managers, builders, strata committees, retailers and hospitality operators across the region.",
      "Two things matter on commercial sites: keeping you trading, and finishing on schedule. We routinely run nights, weekends and split shifts to minimise disruption to tenants, customers and staff. Our crews are fully insured ($20M public liability), inducted and SWMS-ready for any commercial site.",
      "Project documentation, scope clarity and milestone reporting are standard. You'll always know where the job is at, what's been done, and what's coming next.",
    ],
    images: [
      "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
    bullets: [
      "Out-of-hours scheduling to keep you trading",
      "Strata and body corporate experience",
      "Fully insured, induction-ready crews",
      "Project documentation, scope and milestone reporting",
    ],
    faqs: [
      { q: "Do you work after hours?", a: "Yes — we routinely run nights and weekends for retail, hospitality and tenanted commercial spaces to minimise disruption." },
      { q: "Are you insured for commercial sites?", a: "Yes. $20M public liability and full workers compensation. SWMS and inductions provided on request." },
    ],
  },
  {
    slug: "driveway-sealing-epoxy",
    name: "Driveway Sealing & Epoxy",
    title: "Driveway Sealing & Garage Epoxy Floors — Sunshine Coast",
    short: "Concrete driveway sealing and decorative epoxy fleck floors for garages, patios and outdoor entertaining areas.",
    hero: "/api/uploads/client/epoxy-closeup.jpg",
    details: [
      "Concrete driveways, garages and patios are some of the hardest-working surfaces on a property — and the most ignored. Sealing protects the concrete from oil, tyre marks, salt and UV damage, makes the surface dramatically easier to clean, and can completely transform the colour and finish.",
      "We offer two main systems. Penetrating concrete sealers (matt, satin or wet-look) suit driveways, paths and exposed-aggregate. They sink into the concrete, protect from within, and don't peel. Decorative epoxy fleck systems suit garages, patios and feature floors — a thicker, multi-coat decorative finish with coloured flake and a clear UV-stable topcoat.",
      "Every job starts with a deep clean, etch or grind to prep the concrete properly. Cracks are filled, oil stains treated. The finish you walk on at sign-off is built to last — and easy to maintain for years.",
    ],
    images: [
      "/api/uploads/client/epoxy-after.jpg",
      "/api/uploads/client/epoxy-closeup.jpg",
      "/api/uploads/client/driveway-after.jpg",
    ],
    bullets: [
      "High-pressure clean and surface prep",
      "Penetrating concrete sealers — matt, satin or gloss",
      "Decorative epoxy fleck systems for garages and patios",
      "UV-stable, slip-resistant, easy to maintain",
    ],
    faqs: [
      { q: "What's the difference between sealing and epoxy?", a: "Driveway sealing penetrates and protects existing concrete (keeps it clean, easier to maintain, can enhance colour). Epoxy is a thicker decorative coating — usually with coloured flake — applied to garages, patios or feature floors." },
      { q: "How long does an epoxy garage floor take?", a: "Most domestic garages are completed in 2–3 days including grinding, primer, base coat, flake broadcast and clear topcoat. The floor is walk-on ready in 24 hours and car-ready in 5–7 days." },
      { q: "Will sealer change the colour of my driveway?", a: "A wet-look or gloss sealer will darken and enrich the concrete tone. A matt penetrating sealer leaves the look largely unchanged — we'll show you samples before committing." },
    ],
  },
];

export const SUBURBS = [
  {
    slug: "painters-noosa", name: "Noosa", blurb: "Premium residential repaints across Noosa Heads, Noosaville and Sunshine Beach.", postcode: "4567",
    hero: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Connolly's Painting Solutions has been repainting Noosa homes for over 20 years — from beachfront properties in Noosa Heads to hinterland retreats in Doonan and family homes in Noosaville and Sunshine Beach. The Noosa climate is unforgiving on exterior paint (salt, humidity, near-vertical UV), so we specify coastal-grade coating systems designed to hold their finish for the long haul.",
      areas: ["Noosa Heads", "Noosaville", "Sunshine Beach", "Tewantin", "Doonan", "Peregian"],
      whyLocal: "We know Noosa properties — from mid-century Hastings Street apartments through architect-designed Noosa Sound homes. Every quote includes a proper on-site walk-through, honest recommendations on prep, and a fixed price with no surprises.",
    },
  },
  {
    slug: "painters-coolum", name: "Coolum", blurb: "Coastal exteriors built for salt air and surf-edge living.", postcode: "4573",
    hero: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Coolum's beachside homes and hinterland acreage need paint systems that can handle everything from ocean spray to bushland humidity. Our Coolum crews specialise in salt-air-tough exteriors and clean, considered interiors for family homes across Point Arkwright, Yaroomba and Marcus Beach.",
      areas: ["Coolum Beach", "Point Arkwright", "Yaroomba", "Marcus Beach", "Peregian Springs"],
    },
  },
  {
    slug: "painters-peregian", name: "Peregian", blurb: "Beach-house repaints and refined interior work across Peregian Beach and Springs.", postcode: "4573",
    hero: "https://images.unsplash.com/photo-1519046904884-53103b34b206?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Peregian Beach and Peregian Springs sit right on the coastal edge — the paint spec matters. We repaint everything from timber-clad beach shacks in the old village to newer render-and-Colorbond homes across the Springs golf estate. Our exterior systems are UV-stable and mould-resistant; interiors are finished with premium acrylics from Dulux, Haymes and Resene.",
      areas: ["Peregian Beach", "Peregian Springs", "Coolum", "Marcus Beach"],
    },
  },
  {
    slug: "painters-buderim", name: "Buderim", blurb: "Hinterland family homes — refined interiors and protected exteriors.", postcode: "4556",
    hero: "https://images.unsplash.com/photo-1542397284385-6010376c5337?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Buderim is one of our busiest suburbs — a mix of established family homes on the mountain, renovated Queenslanders, and modern acreage builds. We handle full-home repaints, weatherboard restorations, and roof restorations across Buderim, Kunda Park, Sippy Downs and Forest Glen. Every job includes proper prep — filling, sanding, undercoating — before a single top coat goes on.",
      areas: ["Buderim", "Sippy Downs", "Kunda Park", "Forest Glen", "Palmwoods"],
      whyLocal: "Buderim's mountain climate is different to the coast — more moss and mould pressure on exteriors, but less salt. We adjust our exterior wash and coating systems accordingly.",
    },
  },
  {
    slug: "painters-alexandra-headland", name: "Alexandra Headland", blurb: "Beachside apartments and coastal homes near Alex headland.", postcode: "4572",
    hero: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Alex Headland sits right on the surf — every exterior we paint here has to hold up to full salt exposure. Our teams handle beachfront apartment repaints, body corporate common areas and coastal family homes throughout Alexandra Headland and neighbouring Maroochydore and Mooloolaba.",
      areas: ["Alexandra Headland", "Mooloolaba", "Maroochydore", "Cotton Tree"],
    },
  },
  {
    slug: "painters-mooloolaba", name: "Mooloolaba", blurb: "Esplanade apartments and salt-tough exterior systems.", postcode: "4557",
    hero: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85&sat=-15",
    content: {
      intro: "Mooloolaba's Esplanade apartment blocks and canal-front homes see some of the toughest salt exposure on the Sunshine Coast. We repaint everything from single-storey Buderim Avenue houses through to full body corporate repaints on high-rise complexes — always with premium coastal-grade paint systems and out-of-hours scheduling to minimise disruption.",
      areas: ["Mooloolaba", "Alexandra Headland", "Kawana", "Buddina", "Minyama"],
    },
  },
  {
    slug: "painters-bokarina", name: "Bokarina", blurb: "Modern Bokarina Beach homes — sharp interiors, weather-ready exteriors.", postcode: "4575",
    hero: "https://images.unsplash.com/photo-1564540583246-934409427776?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Bokarina Beach's modern architect-designed homes deserve a considered paint spec — clean lines, sharp cutting-in, no roller marks. Our Bokarina crews handle new-build interiors, exterior repaints and body corporate work across the growing Kawana coastal precinct.",
      areas: ["Bokarina", "Wurtulla", "Warana", "Kawana", "Birtinya"],
    },
  },
  {
    slug: "painters-birtinya", name: "Birtinya", blurb: "Lake-edge new builds and refresh projects across Birtinya.", postcode: "4575",
    hero: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Birtinya's lake-edge estate is one of the fastest-growing residential pockets on the Sunshine Coast. We paint new builds and quick-turnaround refresh jobs throughout Birtinya, Bokarina and the neighbouring Oceanside precinct — with a focus on fixed-price quoting and clean handovers.",
      areas: ["Birtinya", "Bokarina", "Wurtulla", "Kawana Waters"],
    },
  },
  {
    slug: "painters-caloundra", name: "Caloundra", blurb: "From Kings Beach to Pelican Waters — repaints with a coastal touch.", postcode: "4551",
    hero: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Caloundra covers a huge range of housing stock — from post-war Kings Beach cottages through to modern Pelican Waters and Golden Beach homes. We handle full-home repaints, roof restorations and exterior weatherboard work across the entire Caloundra district.",
      areas: ["Caloundra", "Kings Beach", "Pelican Waters", "Golden Beach", "Currimundi", "Little Mountain"],
    },
  },
  {
    slug: "painters-kawana", name: "Kawana", blurb: "Kawana Island, Waters and Forest — full-service residential painting.", postcode: "4575",
    hero: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Kawana is one of the Sunshine Coast's largest residential zones — from canal-front homes on Kawana Island through the family suburbs of Kawana Waters and Forest. Our Kawana crews repaint single-storey homes, townhouses and body corporate common areas across the whole precinct.",
      areas: ["Kawana Waters", "Kawana Island", "Warana", "Buddina", "Minyama"],
    },
  },
  {
    slug: "painters-mountain-creek", name: "Mountain Creek", blurb: "Family-home repaints and renovations across Mountain Creek.", postcode: "4557",
    hero: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Mountain Creek's family-friendly streets are lined with 90s and 2000s-era brick-and-render homes — most now due for their second or third repaint. We handle full interior repaints, exterior refreshes and body corporate work throughout Mountain Creek and neighbouring Buddina and Minyama.",
      areas: ["Mountain Creek", "Buddina", "Minyama", "Sippy Downs", "Buderim"],
    },
  },
  {
    slug: "painters-maroochydore", name: "Maroochydore", blurb: "Apartments, townhouses and commercial fit-outs across Maroochydore.", postcode: "4558",
    hero: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    content: {
      intro: "Maroochydore is the commercial heart of the Sunshine Coast — and one of our busiest painting suburbs. Our Maroochydore crews handle everything from beachfront apartment repaints along Cotton Tree and Alexandra Parade through to full body corporate common-area jobs, commercial shopfronts in the CBD, and family-home interiors right across the suburb.",
      areas: ["Maroochydore", "Cotton Tree", "Alexandra Headland", "Kuluin", "Buderim", "Bli Bli"],
      whyLocal: "Because Maroochydore covers everything — coastal high-rises, older Queenslanders, new townhouse developments and commercial premises — we bring the right paint system and crew for the job. We schedule commercial and body corporate work around trading hours or after-hours to keep tenants and businesses running without disruption.",
      services: [
        { label: "Residential interior repaints", slug: "interior-painting" },
        { label: "Coastal exterior painting", slug: "exterior-painting" },
        { label: "Roof restoration", slug: "roof-painting-sunshine-coast" },
        { label: "Body corporate & strata painting", slug: "body-corporate-painters-sunshine-coast" },
        { label: "Commercial shopfront painting", slug: "commercial-painting" },
      ],
    },
  },
];

export const TESTIMONIALS = [
  { name: "Sarah & James M.", suburb: "Noosa Heads", text: "Connolly's Painting repainted the entire exterior of our beachfront home. Two years on, the finish still looks brand new — and the crew were the cleanest tradesmen we've ever had on site.", rating: 5 },
  { name: "Olivia P.", suburb: "Buderim", text: "We had three quotes for a full interior repaint. Connolly's wasn't the cheapest but their level of detail in the walkthrough sold us. Worth every dollar.", rating: 5 },
  { name: "Mark D.", suburb: "Mooloolaba", text: "Our apartment block needed a full repaint and they coordinated everything with our body corporate. Zero issues, on schedule, on budget.", rating: 5 },
  { name: "Rachel & Tom", suburb: "Peregian Beach", text: "We changed our minds on the colour twice — they were patient, recommended a Resene off-white that completely transformed the lounge. Couldn't recommend more.", rating: 5 },
  { name: "Anita L.", suburb: "Caloundra", text: "Roof restoration on a 1990s tile roof. Looks 20 years younger. Communication was excellent and the team was a pleasure to deal with.", rating: 5 },
  { name: "Jordan K.", suburb: "Maroochydore", text: "Quick turnaround on a rental refresh between tenancies. Got it done over a long weekend without compromise. Now my go-to painters.", rating: 5 },
];

export const GALLERY = [
  {
    id: "g1",
    title: "Coastal exterior repaint — Noosa Heads",
    suburb: "Noosa",
    service: "Exterior Painting",
    before: "/api/uploads/client/noosa-before.jpg",
    after: "/api/uploads/client/noosa-after.jpg",
  },
  {
    id: "g2",
    title: "Whole-home interior — Buderim",
    suburb: "Buderim",
    service: "Interior Painting",
    before: "/api/uploads/client/buderim-before.jpg",
    after: "/api/uploads/client/buderim-after.jpg",
  },
  {
    id: "g3",
    title: "Roof restoration — Caloundra",
    suburb: "Caloundra",
    service: "Roof Painting",
    before: "/api/uploads/client/caloundra-roof-before.jpg",
    after: "/api/uploads/client/caloundra-roof-after.jpg",
  },
  {
    id: "g4",
    title: "Beachfront apartment — Mooloolaba",
    suburb: "Mooloolaba",
    service: "Interior Painting",
    before: "/api/uploads/client/mooloolaba-before.jpg",
    after: "/api/uploads/client/mooloolaba-after.jpg",
  },
  {
    id: "g5",
    title: "New build — Coastal home",
    suburb: "Bokarina",
    service: "Interior Painting",
    before: "/api/uploads/client/newbuild-before.jpg",
    after: "/api/uploads/client/newbuild-after.jpg",
  },
  {
    id: "g6",
    title: "Commercial repaint — Maroochydore",
    suburb: "Maroochydore",
    service: "Commercial Painting",
    before: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    after: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
  },
  {
    id: "g7",
    title: "Garage epoxy fleck floor",
    suburb: "Sunshine Coast",
    service: "Driveway Sealing & Epoxy",
    before: "/api/uploads/client/epoxy-before.jpg",
    after: "/api/uploads/client/epoxy-after.jpg",
  },
  {
    id: "g8",
    title: "Driveway clean & seal",
    suburb: "Sunshine Coast",
    service: "Driveway Sealing & Epoxy",
    before: "/api/uploads/client/driveway-before.jpg",
    after: "/api/uploads/client/driveway-after.jpg",
  },
];

export const PROPERTY_TYPES = [
  "Single-storey home",
  "Double-storey home",
  "Apartment / Unit",
  "Townhouse",
  "Commercial premises",
  "Rental / Investment property",
  "Other",
];
